import { useEntityRefLink } from '@backstage/plugin-catalog-react';
import {
  Alert,
  CellText,
  type ColumnConfig,
  Flex,
  Table,
  Text,
  useTable,
} from '@backstage/ui';
import { AccessReport, AccessRow } from '../api';

type Row = AccessRow & { id: string };

const SCOPE_KINDS: Record<string, string> = {
  subscription: 'whole subscription',
  resourceGroup: 'resource group',
  resource: 'single resource',
  other: 'above the subscriptions',
};

/**
 * One access report as a table. Read the "held by" column according to what
 * the report is about: for a person, group or service principal it says
 * whether the role is theirs directly or comes through a group; for a resource
 * group or system it names who holds the role.
 */
export const AccessReportView = ({ report }: { report: AccessReport }) => {
  const linkTo = useEntityRefLink();
  const href = (ref?: string) => (ref ? linkTo(ref) : undefined);
  const aboutPrincipal = report.targetKind === 'principal';

  const columns: ColumnConfig<Row>[] = [
    {
      id: 'role',
      label: 'Role',
      isRowHeader: true,
      cell: row => (
        <CellText
          title={row.role}
          description={
            row.privileged
              ? 'privileged — can change things or grant access'
              : undefined
          }
        />
      ),
    },
    {
      id: 'scope',
      label: 'On',
      cell: row => (
        <CellText
          title={row.scope.label}
          description={SCOPE_KINDS[row.scope.kind] ?? row.scope.kind}
          href={href(row.scope.entityRef)}
        />
      ),
    },
    {
      id: 'holder',
      label: aboutPrincipal ? 'How' : 'Held by',
      cell: row =>
        aboutPrincipal ? (
          <CellText
            title={
              row.inherited ? `through ${row.principal.label}` : 'directly'
            }
            description={row.inherited ? row.principal.type : undefined}
            href={row.inherited ? href(row.principal.entityRef) : undefined}
          />
        ) : (
          <CellText
            title={row.principal.label}
            description={`${row.principal.type}${
              row.inherited ? ' · from the subscription' : ''
            }`}
            href={href(row.principal.entityRef)}
          />
        ),
    },
  ];

  const data: Row[] = report.rows.map((row, index) => ({
    ...row,
    id: String(index),
  }));
  const { tableProps } = useTable({
    mode: 'complete',
    data,
    paginationOptions: { pageSize: data.length || 1 },
  });

  const privileged = report.rows.filter(r => r.privileged).length;
  const inherited = report.rows.filter(r => r.inherited).length;
  const summary = [
    `${report.rows.length} role assignment${
      report.rows.length === 1 ? '' : 's'
    }`,
    `${privileged} privileged`,
    aboutPrincipal
      ? `${inherited} through groups`
      : `${inherited} from the subscription`,
  ].join(' · ');

  return (
    <Flex direction="column" gap="4">
      <Text>{summary}</Text>
      {report.notes.map(note => (
        <Alert key={note} status="info" icon title={note} />
      ))}
      {report.rows.length === 0 ? (
        <Alert status="info" icon title="No role assignments" />
      ) : (
        <Table
          columnConfig={columns}
          {...tableProps}
          pagination={{ type: 'none' }}
        />
      )}
    </Flex>
  );
};
